// ============================================================================
// Feature flags — per-client (per-owner) SaaS module toggles.
//
// The super-admin enables/disables whole modules per account from the admin
// panel (Manage Features). The app reads the current owner's enabled features
// to drive the dynamic nav, route guards, and API access. Flags are keyed to
// the account OWNER (see lib/tenant.js), so team members inherit the owner's
// modules. Mirrors the RBAC catalogue pattern in lib/permissions.js.
// ============================================================================
import { supabase } from './supabase';
import { getOwnerId } from './tenant';

// Static catalogue (must mirror the seeds in backend/feature_management.sql).
// `icon` is a lucide-react component name; consumers map it as needed.
export const FEATURE_CATALOG = [
  { key: 'billing',            name: 'Billing',             icon: 'FileText',      category: 'core' },
  { key: 'reparto',            name: 'Reparto',             icon: 'Truck',         category: 'core' },
  { key: 'reports',            name: 'Reports',             icon: 'BarChart3',     category: 'core' },
  { key: 'material_management', name: 'Material Management', icon: 'Boxes',         category: 'erp' },
  { key: 'production',         name: 'Production',          icon: 'Factory',       category: 'erp' },
  { key: 'inventory',          name: 'Inventory',           icon: 'Warehouse',     category: 'erp' },
  { key: 'employee',           name: 'Employee',            icon: 'Users',         category: 'erp' },
  { key: 'purchases',          name: 'Purchases',           icon: 'ShoppingCart',  category: 'erp' },
  { key: 'dispatch',           name: 'Dispatch',            icon: 'Send',          category: 'erp' },
  { key: 'customer_orders',    name: 'Customer Orders',     icon: 'ClipboardList', category: 'erp' },
  { key: 'quality_control',    name: 'Quality Control',     icon: 'ShieldCheck',   category: 'erp' },
  { key: 'fbr_invoicing',      name: 'FBR Digital Invoicing', icon: 'ScanLine',    category: 'fiscal' },
];

// Per-owner cache so the nav/guards don't refetch on every mount. Keyed by
// owner id; cleared on logout / when the active account changes (impersonation).
let _cache = null; // { ownerId, keys: Set<string> }

export function clearFeatureCache() {
  _cache = null;
}

// Resolve the current account owner's ENABLED feature keys as a Set.
// RLS lets the owner and their team read their own client_features rows.
export async function fetchClientFeatures() {
  const ownerId = await getOwnerId();
  if (!ownerId) return new Set();
  if (_cache && _cache.ownerId === ownerId) return _cache.keys;
  const { data, error } = await supabase
    .from('client_features')
    .select('enabled, features!inner(feature_key)')
    .eq('owner_id', ownerId)
    .eq('enabled', true);
  if (error) {
    // Fail closed on gated modules but don't crash the app if the table/policy
    // isn't there yet — return an empty set so only ungated nav shows.
    console.warn('fetchClientFeatures failed:', error.message);
    return new Set();
  }
  const keys = new Set((data || []).map((r) => r.features?.feature_key).filter(Boolean));
  _cache = { ownerId, keys };
  return keys;
}
